import React from 'react';
import Link from 'next/link';
import SEOHeading from '@/components/common/SEOHeading';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Puppy {
  id: number;
  name: string;
  variety: string;
  gender: string;
  age: string;
  price: number;
  image: string;
  alt: string;
  available: boolean;
}

const mockPuppies: Puppy[] = [
  {
    id: 1,
    name: 'FINN',
    variety: 'Yellow Labrador',
    gender: 'Male',
    age: '10 weeks',
    price: 1800,
    image: '/assets/images/finn_00.jpg',
    alt: 'Beautiful Yellow Labrador Retriever puppy for sale - AKC-certified male Lab puppy from champion bloodlines',
    available: true,
  },
  {
    id: 2,
    name: 'JAX',
    variety: 'Black Labrador',
    gender: 'Male',
    age: '12 weeks',
    price: 1900,
    image: '/assets/images/Jax_00-1769774272160.jpg',
    alt: 'Handsome Black Labrador Retriever puppy for sale - AKC-certified male Black Lab puppy with shiny coat',
    available: true,
  },
  {
    id: 3,
    name: 'CoCo',
    variety: 'Yellow Labrador',
    gender: 'Female',
    age: '9 weeks',
    price: 2000,
    image: '/assets/images/Coco_00-1769774357121.jpg',
    alt: 'Adorable Yellow Labrador Retriever puppy for sale - female Yellow Lab puppy from reputable breeders',
    available: true,
  },
  {
    id: 4,
    name: 'LuLu',
    variety: 'Chocolate Labrador',
    gender: 'Female',
    age: '11 weeks',
    price: 2100,
    image: '/assets/images/Lulu_00-1769774475882.jpg',
    alt: 'Stunning Chocolate Labrador Retriever puppy for sale - female Chocolate Lab puppy with champion parents',
    available: true,
  },
  {
    id: 5,
    name: 'Bonley',
    variety: 'Chocolate Labrador',
    gender: 'Male',
    age: '8 weeks',
    price: 1750,
    image: '/assets/images/Bonley_00-1769774580806.jpg',
    alt: 'Playful Chocolate Labrador Retriever puppy for sale - male Chocolate Lab puppy with light brown coat',
    available: true,
  },
  {
    id: 6,
    name: 'LiLi',
    variety: 'Yellow Labrador',
    gender: 'Female',
    age: '13 weeks',
    price: 1850,
    image: '/assets/images/Lili_00-1769774668713.jpg',
    alt: 'Energetic Yellow Labrador Retriever puppy for sale - female Yellow Lab puppy ready for new home',
    available: true,
  },
  {
    id: 7,
    name: 'BRUNO',
    variety: 'Yellow Labrador',
    gender: 'Male',
    age: '10 weeks',
    price: 1950,
    image: '/assets/images/Bruno_00-1769774753991.jpg',
    alt: 'Sweet Yellow Labrador Retriever puppy for sale - male Yellow Lab puppy from English Labrador breeders',
    available: true,
  },
  {
    id: 8,
    name: 'WALTERS',
    variety: 'Chocolate Labrador',
    gender: 'Male',
    age: '9 weeks',
    price: 2050,
    image: '/assets/images/Walters_00-1769775257641.jpg',
    alt: 'Beautiful Chocolate Labrador Retriever puppy for sale - male Chocolate Lab puppy with health guarantee',
    available: true,
  },
];

const FeaturedPuppies = () => {
  return (
    <section className="border-t border-border bg-muted/30 py-16">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <SEOHeading level={2} className="mb-4">
            Meet Our Available Labrador Puppies - AKC-Certified & Ready for Their Forever Homes
          </SEOHeading>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Take a look at our current litter of adorable Lab puppies! Each one is AKC-registered
            with champion parents and comes with our rock-solid 2-year health guarantee. From
            classic Yellow Labs to striking Black Labs, rich Chocolate Labs, and rare Fox Red Labs,
            these healthy, happy puppies are ready to become part of your family.
          </p>
        </div>

        {/* Puppies Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {mockPuppies.map((puppy, index) => (
            <div
              key={puppy.id}
              className="group overflow-hidden rounded-xl bg-card shadow-warm transition-smooth hover:shadow-warm-lg"
            >
              {/* Image with explicit dimensions for CLS prevention */}
              <div className="relative aspect-[3/4] overflow-hidden bg-muted/20">
                <AppImage
                  src={puppy.image}
                  alt={puppy.alt}
                  width={400}
                  height={533}
                  priority={index < 3}
                  quality={85}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  className="h-full w-full object-contain transition-smooth group-hover:scale-105"
                />

                {puppy.available && (
                  <div className="absolute right-3 top-3 rounded-full bg-success px-3 py-1 text-xs font-semibold text-success-foreground shadow-warm">
                    Available
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="mb-3 flex items-start justify-between">
                  <div>
                    <h3 className="mb-1 font-heading text-xl font-semibold text-foreground">
                      {puppy.name}
                    </h3>
                    <p className="caption text-muted-foreground">{puppy.variety}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl font-bold text-primary">From $500 - $700</p>
                  </div>
                </div>

                {/* Details */}
                <div className="mb-4 flex flex-wrap gap-3">
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Icon name="UserIcon" size={16} variant="outline" />
                    {puppy.gender}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Icon name="CalendarIcon" size={16} variant="outline" />
                    {puppy.age}
                  </div>
                </div>

                {/* Action Button */}
                <Link
                  href="/available-puppies"
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-accent font-medium text-accent-foreground transition-smooth hover:scale-[0.97] hover:shadow-warm focus-ring"
                >
                  View Details
                  <Icon name="ArrowRightIcon" size={18} variant="outline" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="mt-12 text-center">
          <Link
            href="/available-puppies"
            className="inline-flex h-14 items-center justify-center gap-2 rounded-md border-2 border-primary bg-transparent px-8 font-semibold text-primary transition-smooth hover:bg-primary hover:text-primary-foreground focus-ring"
          >
            See All Our Puppies
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedPuppies;
